import { type Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Overline } from '@/components/atoms/Eyebrow';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import {
    type AllocationSummary as AllocationSummaryData,
    parseAllocationCount,
    type WizardValidationError,
} from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';
import { ValidationBanner } from '../shared/ValidationBanner';

import { TargetCard } from './SplitTargetCard';

import styles from '../../operateModals.module.scss';

interface Props {
    initialSource: WizardSource;
    /** Empty cages available as target candidates. */
    emptyCages: OverviewCage[];
    /** The remainder destination, when the operator kept it in Step 1. */
    remainder: WizardDestination | null;
    /** Destinations the operator added beyond the remainder. */
    targets: WizardDestination[];
    errors: WizardValidationError[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Split mode — Step 2 (Split targets).
 *
 * The operator picks the cage(s) that receive the split portion of the
 * initial fish group. Each target gets a card with name + cage + estimated
 * count + notes, mirroring the destination form used elsewhere.
 *
 * The minimum number of target cards depends on the Step 1 decision:
 *  - Continuation **kept** → ≥ 1 target (continuation + target = 2 destinations,
 *    enough for the backend to infer `split`).
 *  - Continuation **vacated** → ≥ 2 targets (the only destinations; a single
 *    target would degenerate into a relocate / merge).
 *
 * `stepCanProceed` enforces `destinations.length >= 2` for this step, which
 * collapses to the right bound in both cases.
 *
 * The allocation summary is scoped to the merge-target pool: how much fish
 * is available for the targets (initial − continuation, or initial when the
 * remainder is off) and how much the operator has assigned across them.
 * Different from the global summary used elsewhere; same semantics as the
 * Split-and-Merge target step.
 */
export default function SplitTargetsStep(props: Props) {
    const { initialSource, emptyCages, remainder, targets, errors, populationById, dispatch } =
        props;
    const { t } = useTranslation('fishGroups');

    // When the operator vacated the source cage in Step 1, two destinations
    // are the minimum: one would degenerate into a relocate. We pre-seed
    // empty target rows here so the form is ready out of the box instead of
    // requiring the operator to click "Add target" twice. The action is
    // idempotent in the reducer — noop when ≥2 targets already exist or when
    // a remainder is present — so strict-mode double-firing is harmless.
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
    useEffect(() => {
        dispatch({ type: 'ensureSplitTargets' });
    }, []);

    // Scoped allocation summary: the pool is what's available for the
    // targets (everything not kept by the continuation). The breakdown is
    // hidden — a single-line "Initial − continuation" item duplicates the
    // headline figure without adding information.
    const initialPopulation = populationById.get(initialSource.fishGroupId)?.initialPopulationSize;
    const continuationCount = remainder
        ? (parseAllocationCount(remainder.estimatedFishCount) ?? 0)
        : 0;
    const pool =
        initialPopulation != null ? Math.max(0, initialPopulation - continuationCount) : null;
    let totalAllocated = 0;
    for (const dest of targets) {
        const n = parseAllocationCount(dest.estimatedFishCount);
        if (n != null) totalAllocated += n;
    }
    let summaryRemaining: number | null = null;
    let summaryOverage: number | null = null;
    if (pool != null) {
        const diff = pool - totalAllocated;
        if (diff >= 0) summaryRemaining = diff;
        else summaryOverage = -diff;
    }
    const allocationSummary: AllocationSummaryData = {
        totalSourcePopulation: pool,
        anySourceUnknown: pool == null,
        totalAllocated,
        remaining: summaryRemaining,
        overage: summaryOverage,
    };

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>{t('actions.operate.split.targets.heading')}</h4>
                <p className={styles.description}>
                    {remainder
                        ? t('actions.operate.split.targets.description.withRemainder')
                        : t('actions.operate.split.targets.description.vacated')}
                </p>
            </div>

            <AllocationSummary
                summary={allocationSummary}
                // No per-source breakdown in this step: the single "moved
                // from initial" item would duplicate the headline number.
                sources={[]}
                populationById={populationById}
                titleKey="actions.operate.split.targets.allocationTitle"
            />

            {errors.length > 0 && <ValidationBanner errors={errors} />}

            <div className={styles.targetsSection}>
                <Overline as="h5" size="md" className={styles.subSectionTitle}>
                    {t('actions.operate.split.targetsHeading')}
                </Overline>
                {targets.length === 0 ? (
                    <EmptyState
                        title={t('actions.operate.split.targetsEmpty')}
                        appearance="quiet"
                    />
                ) : (
                    <div className={styles.destinationsList}>
                        {targets.map((dest, index) => (
                            <TargetCard
                                key={dest.tempId}
                                index={index}
                                destination={dest}
                                emptyCages={emptyCages}
                                sourceName={initialSource.name}
                                dispatch={dispatch}
                            />
                        ))}
                    </div>
                )}

                <Button
                    variant="secondary"
                    size="small"
                    type="button"
                    className={styles.addButton}
                    onClick={() => dispatch({ type: 'addDestination' })}>
                    {t('actions.operate.split.addButton')}
                </Button>
            </div>
        </section>
    );
}
